import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	Button,
	Card,
	Col,
	Flex,
	Form,
	notification,
	Row,
	Steps,
	Typography,
} from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";

import { GET } from "../../../providers/useAxiosQuery";
import {
	checkNetworkStatus,
	readFile,
} from "../../../providers/useCapacitorStorage";
import ModalPreviewAnswer from "./components/ModalPreviewAnswer";
import QuestionContent from "./components/QuestionContent";
import backToTop from "../../../providers/backToTop";
import currentLocation from "../../../providers/currentLocation";

const sortFormByOrder = (formData) => {
	if (!formData) return formData;

	const sortedCategories = [...(formData.form_question_categories || [])]
		.sort((a, b) => {
			const aOrder = Number(a?.order_no ?? 0);
			const bOrder = Number(b?.order_no ?? 0);
			return aOrder - bOrder;
		})
		.map((category) => ({
			...category,
			form_questions: [...(category.form_questions || [])].sort((a, b) => {
				const aOrder = Number(a?.order_no ?? 0);
				const bOrder = Number(b?.order_no ?? 0);
				return aOrder - bOrder;
			}),
		}));

	return {
		...formData,
		form_question_categories: sortedCategories,
	};
};

export default function PageSurveyForm() {
	const params = useParams();
	const navigate = useNavigate();
	const [form] = Form.useForm();
	const [toggleModalPreview, setToggleModalPreview] = useState(false);
	const [steps, setSteps] = useState([]);
	const [currentStep, setCurrentStep] = useState(0);
	const [stepPercent, setStepPercent] = useState(0);
	const [dataSource, setDataSource] = useState(null);
	const [dataQuestionAll, setDataQuestionAll] = useState([]);
	const [dataQuestion, setDataQuestion] = useState([]);
	const [answers, setAnswers] = useState(null);

	useEffect(() => {
		currentLocation();
	}, []);

	GET(
		`api/form/${params.form_id}`,
		`form_detail_${params.form_id}`,
		async (res) => {
			const formPayload = res?.data?.data ?? res?.data;
			const formData = Array.isArray(formPayload)
				? formPayload[0]
				: formPayload;

			if (!formData) return;

			const findForm = sortFormByOrder(formData);
			console.log("form_detail find Form: ", findForm);

			if (!findForm) return;

			setDataSource(findForm);

			let stepsTemp = [];
			let allQuestions = [];
			findForm.form_question_categories.forEach((item, index) => {
				allQuestions.push(...item.form_questions);
				stepsTemp.push({
					title: <div className="title">{item.category}</div>,
					content: item,
				});
				if (index === 0) {
					setDataQuestion(item.form_questions);
				}
			});

			setSteps(stepsTemp);
			setDataQuestionAll(allQuestions);
		},
		false,
	);

	useEffect(() => {
		const handleReadFile = async () => {
			try {
				let resCheckNetworkStatus = await checkNetworkStatus();

				if (resCheckNetworkStatus?.connected) {
					return;
				}

				try {
					let resReadFile = await readFile("fsuu_dsac_survey_sk/form_list.txt");

					if (resReadFile && resReadFile.data) {
						resReadFile = JSON.parse(resReadFile.data);
						const findForm = sortFormByOrder(
							resReadFile.find((f) => Number(f.id) === Number(params.form_id)),
						);

						if (findForm) {
							let stepsTemp = [];
							let allQuestions = [];

							findForm.form_question_categories.forEach((item) => {
								allQuestions.push(...item.form_questions);
								stepsTemp.push({
									title: <div className="title">{item.category}</div>,
									content: item,
								});
							});

							setSteps(stepsTemp);
							setDataQuestionAll(allQuestions);
							setDataSource(findForm);
						}
					}
				} catch (error) {
					console.error("Error reading file: ", error);
					notification.warning({
						message: "Warning",
						description:
							"Unable to read the file: either no permission or the file was not found.",
						duration: 10,
					});
				}
			} catch (error) {
				console.error("Error checking network status: ", error);
			}
		};

		handleReadFile();

		return () => {};
	}, [params.form_id]);

	const onFinish = (values) => {
		if (!dataSource) return;

		let newAnswers = { ...(answers || {}), ...(values || {}) };

		console.log("onFinish newAnswers: ", newAnswers);
		setAnswers(newAnswers);

		if (currentStep !== dataSource.form_question_categories.length - 1) {
			let currentStepTemp = currentStep + 1;

			let form_question_categories = dataSource.form_question_categories;
			let form_questions =
				form_question_categories[currentStepTemp]?.form_questions || [];

			setDataQuestion(form_questions);

			setCurrentStep(currentStepTemp);
		} else {
			setToggleModalPreview(true);
		}

		setStepPercent(0);

		backToTop(".private-layout");
	};

	const onValuesChange = (changedValues, allValues) => {
		if (!dataSource || !allValues) return;

		let form_question_categories = dataSource.form_question_categories;

		if (currentStep !== form_question_categories.length - 1) {
			let form_questions =
				form_question_categories[currentStep]?.form_questions || [];
			let count = form_questions.length || 1;
			let percent = 100 / count;
			let answered = 0;

			Object.keys(allValues).forEach((key) => {
				if (!key.includes("_other") && !key.includes("qo")) {
					if (allValues[key] !== undefined) {
						let q_id = key.split("_")[1];
						answered += dataQuestionAll.filter(
							(item) => Number(item.id) === Number(q_id),
						).length;
					}
				}
			});

			setStepPercent(percent * answered);
		}
	};

	return (
		<Form
			form={form}
			onFinish={onFinish}
			scrollToFirstError={true}
			onValuesChange={onValuesChange}
		>
			<Row gutter={[12, 0]}>
				<Col
					xs={24}
					sm={24}
					md={24}
					lg={24}
					xl={24}
					className="form-name-wrapper"
				>
					<Button
						type="link"
						className="btn-back"
						icon={<FontAwesomeIcon icon={faArrowLeft} />}
						onClick={() => navigate(-1)}
					/>
					<div className="form-name">
						<span className="form-eyebrow">DSAC SURVEY</span>
						<Typography.Title level={5}>
							{dataSource && dataSource.form_name}
						</Typography.Title>
					</div>
				</Col>

				<Col xs={24} sm={24} md={24} lg={24} xl={24}>
					{steps.length > 0 ? (
						<Card
							title={
								<>
									<div className="section-heading">
										<div className="title-wrapper">
											{steps[currentStep].title}
										</div>
										<span className="step-count">
											{currentStep + 1} / {steps.length}
										</span>
									</div>
									<Steps
										current={currentStep}
										percent={stepPercent}
										items={steps}
									/>
								</>
							}
						>
							<div className="steps-mobile-wrapper">
								<Steps
									current={currentStep}
									percent={stepPercent}
									items={steps}
								/>
							</div>

							<div className="question-wrapper">
								{dataQuestion && dataQuestion.length ? (
									<Row gutter={[20, 0]}>
										{dataQuestion.map((item, index) => {
											return (
												<Col
													xs={24}
													sm={24}
													md={24}
													lg={24}
													xl={24}
													key={index}
												>
													<QuestionContent
														namePath={[`q_${item.id}`]}
														question={item.question}
														description={item.description}
														type={item.question_type}
														required={item.required ? true : false}
														max_checkbox={
															item.max_checkbox ? item.max_checkbox : 0
														}
														escape_questions={
															item.escape_questions ? item.escape_questions : []
														}
														add_another={item.add_another ? true : false}
														form_question_options={
															item.form_question_options.length
																? item.form_question_options
																: []
														}
													/>
												</Col>
											);
										})}
									</Row>
								) : null}
							</div>
						</Card>
					) : null}
				</Col>

				<Col xs={24} sm={24} md={24} lg={24} xl={24}>
					<Flex gap={12} justify="flex-end" className="survey-navigation">
						<Button
							disabled={currentStep === 0}
							onClick={() => {
								if (currentStep > 0) {
									let currentStepTemp = currentStep - 1;

									let count = dataQuestion.length;
									let percent = 100 / count;
									let form_question_categories =
										dataSource.form_question_categories;
									let answered = 0;
									let form_questions =
										form_question_categories[currentStepTemp].form_questions;

									const currentAnswers = answers || {};
									Object.keys(currentAnswers).forEach((key) => {
										if (!key.includes("_other") && !key.includes("qo")) {
											if (currentAnswers[key] !== undefined) {
												let q_id = key.split("_")[1];
												answered += form_questions.filter(
													(item) => Number(item.id) === Number(q_id),
												).length;
											}
										}
									});

									setStepPercent(percent * answered);
									setCurrentStep(currentStepTemp);
									setDataQuestion(form_questions);

									form.setFieldsValue(answers);
								} else {
									setCurrentStep(0);
								}

								backToTop(".private-layout");
							}}
						>
							Back
						</Button>

						{currentStep ===
						(dataSource && dataSource.form_question_categories.length - 1) ? (
							<Button
								htmlType="submit"
								type="primary"
								className="btn-main-primary"
							>
								Preview
							</Button>
						) : (
							<Button
								htmlType="submit"
								type="primary"
								className="btn-main-primary"
							>
								Next
							</Button>
						)}
					</Flex>
				</Col>
			</Row>

			<ModalPreviewAnswer
				toggleModalPreview={toggleModalPreview}
				setToggleModalPreview={setToggleModalPreview}
				dataSource={dataSource}
				answers={answers}
				dataQuestionAll={dataQuestionAll}
			/>
		</Form>
	);
}
